import { Component } from '@theme/component';

const ACTIVE_CLASS = 'is-active';

/* Must match the `.video-hero-carousel__video` opacity transition duration in the section stylesheet. */
const FADE_DURATION_MS = 1600;

/**
 * A full-width hero that crossfades between several looping background videos.
 *
 * Only the active video and the one about to crossfade in next are ever assigned
 * a real `src` and playing at the same time; the rest stay unloaded and paused.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement[]} videoSlides - The stacked background videos.
 *
 * @extends Component<Refs>
 */
export class VideoHeroCarouselComponent extends Component {
  requiredRefs = ['videoSlides'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSlides } = this.refs;
    if (videoSlides.length < 2) return;

    this.activeIndex = videoSlides.findIndex((slide) => slide.classList.contains(ACTIVE_CLASS));
    if (this.activeIndex === -1) this.activeIndex = 0;

    this.#prime(this.activeIndex);
    this.#prime(this.#nextIndex());

    const interval = Number(this.getAttribute('interval')) || 5000;
    this.timer = window.setInterval(() => this.#advance(), interval);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.timer);
  }

  /**
   * @param {number} [offset]
   */
  #nextIndex(offset = 1) {
    const { videoSlides } = this.refs;
    return (this.activeIndex + offset) % videoSlides.length;
  }

  /**
   * Loads (if needed) and plays the video at the given index, restarting it from the beginning.
   *
   * @param {number} index
   */
  #prime(index) {
    const { videoSlides } = this.refs;
    const video = videoSlides[index];
    const source = video.querySelector('source');

    if (source instanceof HTMLSourceElement && !source.getAttribute('src') && source.dataset.src) {
      source.setAttribute('src', source.dataset.src);
      video.load();
    }

    video.currentTime = 0;
    video.play().catch(() => {});
  }

  #advance() {
    const { videoSlides } = this.refs;
    const previousIndex = this.activeIndex;
    const newActiveIndex = this.#nextIndex();

    videoSlides[newActiveIndex].classList.add(ACTIVE_CLASS);
    videoSlides[previousIndex].classList.remove(ACTIVE_CLASS);

    this.activeIndex = newActiveIndex;

    this.#prime(this.#nextIndex());

    window.setTimeout(() => videoSlides[previousIndex].pause(), FADE_DURATION_MS);
  }
}

if (!customElements.get('video-hero-carousel-component')) {
  customElements.define('video-hero-carousel-component', VideoHeroCarouselComponent);
}
